import axios from 'axios';
import { config } from '../config';
import SessionService from './SessionService';
import appCache from './AppCache';
import { downloadXlsxResponse, readFetchErrorMessage } from '../utils/downloadBlob';

const API_URL = config.API_BASE_URL;
const UNOFFICIAL_API_URL = config.UNOFFICIAL_API_URL;

export interface WhatsAppConnection {
  id: number;
  sectorId: number;
  status: string;
  qrcodeBase64: string | null;
  lastQrcodeGeneratedAt: string | null;
  lastConnectedAt: string | null;
  lastDisconnectedAt: string | null;
  sessionData: any;
  createdAt: string;
  updatedAt: string;
}

export interface WhatsAppStatus {
  connected: boolean;
  message: string;
  status: 'connecting' | 'connected' | 'disconnected' | 'error';
  last_contact_sync?: string;
  last_group_sync?: string;
}

export interface WhatsAppGroupDto {
  id: number;
  contactId?: number;
  whatsappGroupId?: number;
  jid: string;
  name: string;
  participantCount: number;
  avatarUrl?: string;
  lastMessage?: string;
  lastMessageTime?: string;
}

export function normalizeWhatsAppGroup(raw: Record<string, unknown>): WhatsAppGroupDto | null {
  const contactId = Number(raw.contactId ?? raw.contact_id ?? 0) || undefined;
  const whatsappGroupId = Number(raw.whatsappGroupId ?? raw.whatsapp_group_id ?? raw.id ?? 0) || undefined;
  const listId = contactId ?? whatsappGroupId;
  if (!listId) return null;

  const jid = String(raw.jid ?? raw.Jid ?? '').trim();
  const name = String(raw.name ?? raw.Name ?? (jid || 'Group')).trim();

  return {
    id: listId,
    contactId,
    whatsappGroupId,
    jid,
    name: name || jid || 'Group',
    participantCount: Number(raw.participantCount ?? raw.participant_count ?? 0) || 0,
    avatarUrl: (raw.avatarUrl ?? raw.avatar_url ?? undefined) as string | undefined,
    lastMessage: (raw.lastMessage ?? raw.last_message ?? undefined) as string | undefined,
    lastMessageTime: (raw.lastMessageTime ?? raw.last_message_time ?? undefined) as string | undefined,
  };
}

export function normalizeWhatsAppGroups(data: unknown): WhatsAppGroupDto[] {
  if (!Array.isArray(data)) return [];
  const out: WhatsAppGroupDto[] = [];
  for (const row of data) {
    if (!row || typeof row !== 'object') continue;
    const normalized = normalizeWhatsAppGroup(row as Record<string, unknown>);
    if (normalized) out.push(normalized);
  }
  return out;
}

export { resolveWhatsAppGroupApiId } from '../utils/broadcastContactUtils';

export interface Sector {
  id: number;
  name: string;
  description: string;
  userBusinessId?: number;
  phoneNumberId: string;
  accessToken: string;
  googleClientId?: string;
  googleApiKey?: string;
  oauth2AccessToken?: string;
  oauth2RefreshToken?: string;
  oauth2TokenExpiration?: Date;
  isOfficial: boolean;
  sparkIsActive?: boolean | null;
  sparkLinePhone?: string | null;
  whatsappConnection?: WhatsAppConnection;
}

export interface CreateSectorRequestDTO {
  name: string;
  description: string;
  userBusinessId?: number;
  phoneNumberId: string;
  accessToken: string;
  googleClientId?: string;
  googleApiKey?: string;
  isOfficial: boolean;
}

export interface UpdateSectorRequestDTO {
  name?: string;
  description?: string;
  userBusinessId?: number;
  phoneNumberId?: string;
  accessToken?: string;
  googleClientId?: string;
  googleApiKey?: string;
  oauth2AccessToken?: string;
  oauth2RefreshToken?: string;
  oauth2TokenExpiration?: Date;
  isOfficial?: boolean;
}

export interface SectorResponse {
  message: string;
  code: string;
  data: Sector[];
}

export interface SingleSectorResponse {
  message: string;
  code: string;
  data: Sector;
}

export const invalidateSectorsCache = (): void => {
  appCache.invalidate('sectors');
  appCache.invalidate('bootstrap');
};

export const setSparkConnectionActive = async (sectorId: number, isActive: boolean): Promise<Sector> => {
  const response = await axios.patch<SingleSectorResponse>(
    `${API_URL}/sectors/${sectorId}/spark-active`,
    { isActive },
    { headers: { Authorization: `Bearer ${SessionService.getToken()}` } }
  );
  invalidateSectorsCache();
  if (!response.data?.data) {
    throw new Error(response.data?.message || 'Could not update Spark connection');
  }
  return mapSparkFields(response.data.data);
};

export const createSector = async (sectorData: CreateSectorRequestDTO) => {
  try {
    const response = await axios.post(`${API_URL}/sectors`, sectorData, {
      headers: {
        'Authorization': `Bearer ${SessionService.getToken()}`
      }
    });
    invalidateSectorsCache();
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateSector = async (id: number, data: UpdateSectorRequestDTO) => {
  try {
    const response = await axios.put(`${API_URL}/sectors/${id}`, data, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SessionService.getToken()}`
      },
    });
    invalidateSectorsCache();
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getSector = async (id: number): Promise<Sector> => {
  try {
    const response = await axios.get<SingleSectorResponse>(`${API_URL}/sectors/${id}`, {
      headers: {
        'Authorization': `Bearer ${SessionService.getToken()}`
      }
    });
    return mapSparkFields(response.data.data);
  } catch (error) {
    throw error;
  }
};

export const getSectors = async (token: string, forceRefresh = false): Promise<Sector[]> => {
  try {
    if (!forceRefresh) {
      const cached = appCache.get<SectorResponse>('sectors');
      if (cached?.data?.length) return cached.data.map(mapSparkFields);
    }

    const response = await axios.get<SectorResponse>(`${API_URL}/sectors`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': '*/*',
      },
    });
    const data = (response.data.data ?? []).map(mapSparkFields);
    appCache.set('sectors', { ...response.data, data }, 10 * 60 * 1000);
    return data;
  } catch (error) {
    throw error;
  }
};

function mapSparkFields(raw: Sector): Sector {
  const r = raw as Sector & { SparkIsActive?: boolean | null; SparkLinePhone?: string | null; IsOfficial?: boolean };
  return {
    ...raw,
    isOfficial: Boolean(r.isOfficial ?? r.IsOfficial ?? false),
    sparkIsActive: r.sparkIsActive ?? r.SparkIsActive ?? null,
    sparkLinePhone: r.sparkLinePhone ?? r.SparkLinePhone ?? null,
  };
}

export const deleteSector = async (id: number) => {
  try {
    const response = await axios.delete(`${API_URL}/sectors/${id}`, {
      headers: {
        'Authorization': `Bearer ${SessionService.getToken()}`
      }
    });
    invalidateSectorsCache();
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getWhatsAppStatus = async (sectorId: number, _isOfficial: boolean): Promise<WhatsAppStatus> => {
  const response = await axios.get(`${API_URL}/whatsapp/status`, {
    params: { sectorId },
    headers: {
      Authorization: `Bearer ${SessionService.getToken()}`
    }
  });
  const data = response.data ?? {};
  const connected = Boolean(data.connected ?? data.Connected);
  const raw = String(data.status ?? data.Status ?? '').toLowerCase();
  const status: WhatsAppStatus['status'] = connected
    ? 'connected'
    : raw === 'connecting' || raw === 'error'
      ? raw
      : 'disconnected';
  return {
    connected,
    message: data.message ?? data.Message ?? '',
    status,
    last_contact_sync: data.last_contact_sync,
    last_group_sync: data.last_group_sync,
  };
};

interface UnofficialQRCodeResponse {
  status: string;
  message: string;
  qrcode?: string;
  data?: {
    instructions?: string;
    qrcode?: string;
  };
  timestamp?: string;
}

interface QRCodeResponse {
  qrcode: string;
  instructions?: string;
  message?: string;
}

export const getWhatsAppQRCode = async (
  sectorId: number,
  _isOfficial: boolean,
  relink?: boolean
): Promise<QRCodeResponse> => {
  const response = await axios.get<UnofficialQRCodeResponse>(`${API_URL}/whatsapp/qrcode`, {
    params: { sectorId, ...(relink ? { relink: 1 } : {}) },
    headers: {
      Authorization: `Bearer ${SessionService.getToken()}`
    }
  });
  const payload = response.data?.data ?? response.data;
  const qrcode = (payload as { qrcode?: string })?.qrcode ?? response.data?.qrcode ?? '';
  const instructions = (payload as { instructions?: string })?.instructions;
  const message = response.data?.message;
  return { qrcode, instructions, message };
};

export const formatWhatsAppQrSrc = (qrcode: string): string => {
  const trimmed = (qrcode || '').trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('data:')) return trimmed;
  return `data:image/png;base64,${trimmed}`;
};

export const getWhatsAppConnection = async (sectorId: number, isOfficial: boolean): Promise<WhatsAppConnection> => {
  try {
    if (isOfficial) {
      // Wire to C# WhatsappIntegrationController: GET /api/whatsapp/connection?sectorId=xx (proxies status/QR support)
      const response = await axios.get(`${API_URL}/whatsapp/connection`, {
        params: { sectorId },
        headers: {
          'Authorization': `Bearer ${SessionService.getToken()}`
        }
      });
      return response.data?.connection || response.data;
    } else {
      const response = await axios.get(`${UNOFFICIAL_API_URL}/whatsapp-connections`, {
        params: { sector_id: sectorId },
        headers: {
          'Authorization': `Bearer ${SessionService.getToken()}`
        }
      });
      return response.data;
    }
  } catch (error) {
    throw error;
  }
};

export const syncWhatsAppContacts = async (sectorId: number): Promise<{ message: string }> => {
  try {
    const response = await axios.post(`${UNOFFICIAL_API_URL}/sync-contacts`, {
      sector_id: sectorId
    }, {
      headers: {
        'Authorization': `Bearer ${SessionService.getToken()}`
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const syncWhatsAppGroups = async (sectorId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axios.post(`${UNOFFICIAL_API_URL}/whatsapp-groups/${sectorId}/sync`, {}, {
      headers: {
        'Authorization': `Bearer ${SessionService.getToken()}`
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getWhatsAppGroups = async (sectorId: number): Promise<{ success: boolean; data: WhatsAppGroupDto[]; count: number }> => {
  try {
    const response = await axios.get(`${UNOFFICIAL_API_URL}/whatsapp-groups/${sectorId}`, {
      headers: {
        'Authorization': `Bearer ${SessionService.getToken()}`
      }
    });
    const raw = response.data;
    const data = normalizeWhatsAppGroups(raw?.data ?? raw);
    return {
      success: Boolean(raw?.success ?? true),
      data,
      count: Number(raw?.count ?? data.length),
    };
  } catch (error) {
    throw error;
  }
};

export const getWhatsAppGroupParticipants = async (
  sectorId: number,
  groupId: number,
  options?: { refresh?: boolean; signal?: AbortSignal }
): Promise<{ success: boolean; data: any[]; count: number; message?: string; resolved_group_jid?: string }> => {
  try {
    const response = await axios.get(`${UNOFFICIAL_API_URL}/whatsapp-groups/${sectorId}/${groupId}/participants`, {
      headers: {
        'Authorization': `Bearer ${SessionService.getToken()}`
      },
      params: options?.refresh ? { refresh: true } : undefined,
      signal: options?.signal,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Downloads .xlsx (Name / Phone Number) with members of the selected WhatsApp groups.
 * Same columns as Contact import template — ready for Spark campaign list upload.
 */
export async function exportWhatsAppGroupParticipants(
  sectorId: number,
  groupIds: number[]
): Promise<{ contactCount: number; groupsProcessed: number; groupsFailed: number }> {
  const token = SessionService.getToken();
  if (!token) throw new Error('Authentication token not found');
  if (sectorId <= 0) throw new Error('Invalid sector');
  if (!groupIds.length) throw new Error('Select at least one group');

  const response = await fetch(
    `${UNOFFICIAL_API_URL}/whatsapp-groups/${sectorId}/export-participants`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ groupIds }),
    }
  );

  if (!response.ok) {
    throw new Error(await readFetchErrorMessage(response, 'Export failed'));
  }

  await downloadXlsxResponse(response, `group_contacts_sector${sectorId}.xlsx`);

  return {
    contactCount: Number(response.headers.get('X-Export-Contact-Count') || 0),
    groupsProcessed: Number(response.headers.get('X-Export-Groups-Processed') || 0),
    groupsFailed: Number(response.headers.get('X-Export-Groups-Failed') || 0),
  };
}
